import { useState, useMemo } from 'react';
import { Client } from '@/types/client';
import { sampleClients } from '@/data/sampleClients';
import { ClientTable } from '@/components/ClientTable';
import { ClientForm } from '@/components/ClientForm';
import { ClientDetailModal } from '@/components/ClientDetailModal';
import { EmailDialog } from '@/components/EmailDialog';
import { DashboardStats } from '@/components/DashboardStats';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Plus, 
  Search, 
  Building2,
  Filter,
  Download,
  RefreshCw,
  Calendar,
  Mail
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const Index = () => {
  const { toast } = useToast();
  const [clients, setClients] = useState<Client[]>(sampleClients);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const [fiscalYear, setFiscalYear] = useState<string>('2024-25');
  const [quarter, setQuarter] = useState<string>('all');

  // Generate fiscal year options (last 5 years and next year)
  const fiscalYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: string[] = [];
    for (let i = -1; i <= 4; i++) {
      const startYear = currentYear - i;
      years.push(`${startYear}-${(startYear + 1).toString().slice(-2)}`);
    }
    return years;
  }, []);
  
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Get unique teams for filter
  const teams = useMemo(() => {
    const uniqueTeams = [...new Set(clients.map(c => c.team).filter(Boolean))];
    return uniqueTeams;
  }, [clients]);

  // Filtered clients
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      const matchesSearch = 
        client.directorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.emailAddress.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.abn.includes(searchQuery);
      
      const matchesStatus = statusFilter === 'all' || 
        client.basStatus === statusFilter ||
        client.bookkeepingStatus === statusFilter;
      
      const matchesTeam = teamFilter === 'all' || client.team === teamFilter;
      
      return matchesSearch && matchesStatus && matchesTeam;
    });
  }, [clients, searchQuery, statusFilter, teamFilter]);

  const handleAddClient = (clientData: Omit<Client, 'id' | 'srNo' | 'createdAt' | 'updatedAt'>) => {
    const newClient: Client = {
      ...clientData,
      id: Date.now().toString(),
      srNo: clients.length + 1,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setClients(prev => [...prev, newClient]);
    toast({
      title: 'Client Added',
      description: `${newClient.companyName} has been added successfully.`,
    });
  };

  const handleEditClient = (clientData: Omit<Client, 'id' | 'srNo' | 'createdAt' | 'updatedAt'>) => {
    if (!selectedClient) return;
    
    setClients(prev => prev.map(c => 
      c.id === selectedClient.id 
        ? { ...c, ...clientData, updatedAt: new Date() }
        : c
    ));
    toast({
      title: 'Client Updated',
      description: `${clientData.companyName} has been updated successfully.`,
    });
    setSelectedClient(null);
  };

  const handleDeleteClient = () => {
    if (!selectedClient) return;
    
    setClients(prev => prev.filter(c => c.id !== selectedClient.id));
    toast({
      title: 'Client Deleted',
      description: `${selectedClient.companyName} has been removed.`,
      variant: 'destructive',
    });
    setDeleteDialogOpen(false);
    setSelectedClient(null);
  };

  const openEditForm = (client: Client) => {
    setSelectedClient(client);
    setFormOpen(true);
  };

  const openDeleteDialog = (client: Client) => {
    setSelectedClient(client);
    setDeleteDialogOpen(true);
  };

  const openDetailModal = (client: Client) => {
    setSelectedClient(client);
    setDetailOpen(true);
  };

  const openEmailDialog = (client: Client) => {
    setSelectedClient(client);
    setEmailDialogOpen(true);
  };

  const handleExport = () => {
    const headers = [
      'Sr. No.', 'Director Name', 'Nature of Business', 'Company Name', 'Client Type',
      'GST Filling', 'ABN', 'TFN', 'Email', 'Team', 'Employee', 'Statement Received',
      'BAS Status', 'BAS Return', 'Payroll', 'STP File', 'Super Payment',
      'Software', 'Handy Ledger', 'Bookkeeping Status', 'Invoices', 'Remarks'
    ];
    
    const csvContent = [
      headers.join(','),
      ...filteredClients.map(c => [
        c.srNo, c.directorName, c.natureOfBusiness, c.companyName, c.clientType,
        c.gstFilling ? 'Yes' : 'No', c.abn, c.tfn, c.emailAddress, c.team, c.employee,
        c.statementReceived ? 'Yes' : 'No', c.basStatus, c.basReturn, c.payrollPayslip,
        c.stpFile, c.superPayment, c.bookkeepingSoftware, c.handyLedger ? 'Yes' : 'No',
        c.bookkeepingStatus, c.invoices, `"${c.remarks}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clients-export.csv';
    a.click();
    
    toast({
      title: 'Export Complete',
      description: `${filteredClients.length} clients exported to CSV.`,
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-primary text-primary-foreground shadow-lg">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center">
                <Building2 className="h-6 w-6 text-secondary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Tax Ready Client Master</h1>
                <p className="text-sm text-primary-foreground/70">Task Management System</p>
              </div>
            </div>
            <Button 
              onClick={() => { setSelectedClient(null); setFormOpen(true); }}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Client
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Stats */}
        <section className="mb-8">
          <DashboardStats clients={clients} />
        </section>

        {/* Filters */}
        <section className="mb-6">
          <div className="flex flex-col gap-4">
            {/* Fiscal Year and Quarter Selection */}
            <div className="flex flex-wrap gap-3 items-center p-4 bg-primary/5 rounded-lg border border-primary/10">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                <span className="font-semibold text-sm text-primary">Report Period:</span>
              </div>
              <Select value={fiscalYear} onValueChange={setFiscalYear}>
                <SelectTrigger className="w-[140px] bg-background">
                  <SelectValue placeholder="Fiscal Year" />
                </SelectTrigger>
                <SelectContent>
                  {fiscalYears.map(year => (
                    <SelectItem key={year} value={year}>FY {year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={quarter} onValueChange={setQuarter}>
                <SelectTrigger className="w-[130px] bg-background">
                  <SelectValue placeholder="Quarter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Quarters</SelectItem>
                  <SelectItem value="Q1">QTR 1 (Jul-Sep)</SelectItem>
                  <SelectItem value="Q2">QTR 2 (Oct-Dec)</SelectItem>
                  <SelectItem value="Q3">QTR 3 (Jan-Mar)</SelectItem>
                  <SelectItem value="Q4">QTR 4 (Apr-Jun)</SelectItem>
                </SelectContent>
              </Select>
              {(fiscalYear !== '2024-25' || quarter !== 'all') && (
                <span className="text-sm text-muted-foreground ml-2">
                  Selected: FY {fiscalYear} {quarter !== 'all' ? `• ${quarter}` : ''}
                </span>
              )}
            </div>

            {/* Search and Filters Row */}
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex flex-1 gap-3 w-full md:w-auto flex-wrap">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search clients by name, company, email, ABN..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={teamFilter} onValueChange={setTeamFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="All Teams" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teams</SelectItem>
                    {teams.map(team => (
                      <SelectItem key={team} value={team}>{team}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={() => { setSearchQuery(''); setStatusFilter('all'); setTeamFilter('all'); setFiscalYear('2024-25'); setQuarter('all'); }}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => selectedClient && openEmailDialog(selectedClient)}
                  disabled={!selectedClient}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </Button>
                <Button variant="outline" onClick={handleExport}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            Showing {filteredClients.length} of {clients.length} clients
          </p>
        </section>

        {/* Client Table */}
        <section>
          <ClientTable
            clients={filteredClients}
            onEdit={openEditForm}
            onDelete={openDeleteDialog}
            onView={openDetailModal}
            onEmail={openEmailDialog}
          />
        </section>
      </main>

      {/* Add/Edit Form */}
      <ClientForm
        open={formOpen}
        onOpenChange={(open) => { setFormOpen(open); if (!open) setSelectedClient(null); }}
        client={selectedClient}
        onSave={selectedClient ? handleEditClient : handleAddClient}
      />

      {/* Detail Modal */}
      <ClientDetailModal
        open={detailOpen}
        onOpenChange={(open) => { setDetailOpen(open); if (!open) setSelectedClient(null); }}
        client={selectedClient}
      />

      {/* Email Dialog */}
      <EmailDialog
        open={emailDialogOpen}
        onOpenChange={(open) => { setEmailDialogOpen(open); if (!open) setSelectedClient(null); }}
        client={selectedClient}
        fiscalYear={fiscalYear}
        quarter={quarter}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedClient?.companyName}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedClient(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteClient}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Index;
